<template>
  <SectionWithAnchor class="row" :anchorId="isFromLLP ? AnchorLinks.FAQ : undefined">
    <div
      :class="[
        isFromLLP
          ? 'col-12 col-md-10 col-lg-8 llp-faq'
          : isHg
            ? 'hg-faq-section'
            : 'is24-faq-section',
      ]"
    >
      <h2 :class="isFromLLP ? 'heading-llp' : 'heading'">
        {{
          t(`${currentPage}.faqs.heading`, {
            propertyType: t(`${currentPage}.propertyType.${propertyType}`),
            location: routeLocationName,
          })
        }}
      </h2>
      <div class="faqs">
        <HgAccordion
          v-for="(item, index) in questionItems"
          :id="`question-${index}`"
          :key="item.question"
          theme="secondary"
          :title="$t(item.question)"
          @click="handleClick($t(item.question))"
        >
          <div v-html="$t(item.answer)" />
        </HgAccordion>
      </div>
    </div>
  </SectionWithAnchor>
</template>

<script setup lang="ts">
import { HgAccordion } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import SectionWithAnchor from '@/components/common/SectionWithAnchor/SectionWithAnchor.vue';
import { useRouting } from '@/composables/useRouting';
import { getLocationInfos } from '@/helpers/GeoLocation/GeoLocationHelper';
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { useCommonStore } from '@/stores/common/common';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { AnchorLinks, Pages, QuestionLimit } from '@/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus/index';

const { t, locale } = useI18n();
useRouting();
const commonStore = useCommonStore();
const localLandingStore = useLocalLandingStore();

const isHg = isPlatformHg();

const { currentPage } = storeToRefs(commonStore);
const { propertyType, routeLocation, routeGeoLocation } = storeToRefs(localLandingStore);

const routeLocationName = computed((): string => {
  return getLocationInfos(routeGeoLocation.value, locale.value as HgSupportedLanguage).name;
});

const isFromLLP = computed(() => {
  return currentPage.value === Pages.LLP;
});

const questionItems = computed(() => {
  return [...Array(isFromLLP.value ? QuestionLimit.LLP : QuestionLimit.LP)].map((_, i) => {
    const index = i + 1;
    return isFromLLP.value
      ? {
          question: `localLanding.faqs.shared.q${index}`,
          answer: `localLanding.faqs.${routeLocation.value}.a${index}`,
        }
      : {
          question: `landing.faqs.q${index}.question`,
          answer: `landing.faqs.q${index}.answer`,
        };
  });
});

const handleClick = (title: string) => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.LINK_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.LINK_CLICK,
    action: LOCAL_LANDING_PAGE_ACTION.OPEN_TIPS,
    label: title,
  });
};
</script>

<style lang="scss" scoped>
@use '@/assets/variables' as *;

.hg-faq-section {
  max-width: 800px;
  margin-right: auto;
  margin-left: auto;
}

.heading {
  margin-top: 0;
  margin-bottom: $space-150;

  @include heading-2;
}

.title {
  margin-top: 0;
  margin-bottom: 0;

  @include heading-3;
}

.description {
  @include heading-4;
}

.is24-faq-section {
  display: flex;
  flex-direction: column;

  @include grid-media-query(sm) {
    flex-direction: row;
    gap: $space-400;
  }

  .heading,
  .faqs {
    flex: 1;
  }
}

.llp-faq {
  display: flex;
  flex-direction: column;
  gap: $space-200;
  margin-right: auto;
  margin-left: auto;
}

.heading-llp {
  @include heading-2;
  @include vertical-margin;
}
</style>
